import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useNow } from '../../hooks/useNow';
import { AlertTriangleIcon, LockIcon, PlusIcon, VideoIcon } from 'lucide-react';
import { useAdmin } from '../AdminContext';
import { ADMIN_STEP_MINUTES, endTime, isActive, layoutLanes, nowMinutes, visibleHours } from '../../core/rules/agenda';
import { toMinutes, toTime } from '../../core/rules/schedule';
import { salonDayFor, staffName } from '../../core/rules/salonDays';
import { staffToneOf } from '../../core/rules/staffColors';
import { StaffDot } from './StaffDot';
import type { StaffProfile } from '../../core/types/site';
import { dayLong, fromIso, today, weekdayShort } from '../../core/format/dates';
import { COMPACT_MINUTES, PX_PER_MINUTE, blockCls, blockLabel, firstName, stateOf } from './agendaRules';
import { useTimelineDrag, type DragTarget } from './useTimelineDrag';
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda';
type Props = {
  days: string[];
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSelectBlock: (block: AgendaBlock) => void;
  /** Klik op een lege plek: nieuwe afspraak op die tijd (kwartier). */
  onEmpty: (date: string, time: string) => void;
  /** Afspraak losgelaten op een nieuwe plek. Zonder: slepen staat uit. */
  onMove?: (target: DragTarget) => void;
  /** Een verzetting die nog bevestigd moet worden: blijft als schaduw staan. */
  pending?: DragTarget | null;
  /** Bij één dag: koppen weglaten (die staan al boven de pagina). */
  showHeader?: boolean;
  /**
   * Gedeelde salon: blokken in de kleur van de uitvoerder met haar initialen, en in de weekkop wie
   * er die dag werkt. Leeg (één persoon) = de gewone statuskleuren.
   */
  team?: StaffProfile[];
};

/** Minstens 36px: tikbaar met één hand en ruimte voor tijd én naam, ook bij 15 minuten. */
const MIN_ITEM_PX = 36;
const itemHeight = (minutes: number) => Math.max(MIN_ITEM_PX, minutes * PX_PER_MINUTE - 3);

/**
 * De dag of week als tijdlijn. Hoogte = tijd, dus een lange behandeling ziet er ook lang uit.
 * Overlap (een bewuste dubbele boeking) komt naast elkaar te staan, nooit onzichtbaar eronder.
 * Geannuleerde afspraken staan hier niet: die tijd is weer vrij (ze staan in het dagoverzicht).
 * Komende afspraken zijn te verslepen (muis/pen, of op de telefoon lang indrukken en slepen);
 * verzetten gebeurt pas na bevestigen.
 */
export function AgendaTimeline({
  days,
  appointments,
  blocks,
  selectedId,
  onSelect,
  onSelectBlock,
  onEmpty,
  onMove,
  pending = null,
  showHeader = true,
  team = []
}: Props) {
  const {
    site,
    index
  } = useAdmin();
  const colored = team.length > 1;
  const hours = site.openingHours;
  const shown = appointments.filter(a => a.outcome !== 'geannuleerd' && days.includes(a.date));
  const shownBlocks = blocks.filter(b => days.includes(b.date));
  const {
    start,
    end
  } = visibleHours(hours, days, [...shown, ...shownBlocks], site.salonExceptions);
  const height = (end - start) * PX_PER_MINUTE;
  const now = useNow();
  const todayIso = today();
  const nowAt = days.includes(todayIso) ? nowMinutes(todayIso, now) : null;
  const nowVisible = nowAt !== null && nowAt >= start && nowAt <= end;
  const drag = useTimelineDrag({
    start,
    end,
    hours,
    appointments,
    blocks,
    now,
    enabled: Boolean(onMove) && !pending,
    onDrop: t => onMove?.(t)
  });
  const ghost = drag.target ?? pending;
  const ghostAppt = ghost ? appointments.find(a => a.id === ghost.id) : undefined;

  // Het kwartier onder de muis: laat zien wat een klik doet ("+ 14:15").
  const [hover, setHover] = useState<{
    date: string;
    minutes: number;
  } | null>(null);
  const quarterAt = (e: MouseEvent<HTMLDivElement>) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const minutes = start + Math.floor(y / PX_PER_MINUTE / ADMIN_STEP_MINUTES) * ADMIN_STEP_MINUTES;
    return Math.max(start, Math.min(minutes, end - ADMIN_STEP_MINUTES));
  };
  const pick = (date: string) => (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    onEmpty(date, toTime(quarterAt(e)));
  };
  const track = (date: string) => (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || drag.dragging) return setHover(h => h ? null : h);
    const minutes = quarterAt(e);
    setHover(h => h && h.date === date && h.minutes === minutes ? h : {
      date,
      minutes
    });
  };

  // Vandaag in beeld: scroll de nu-lijn naar het midden als hij buiten het scherm valt (één keer per dag/week).
  const nowRef = useRef<HTMLSpanElement>(null);
  const dayKey = days.join(',');
  useEffect(() => {
    const el = nowRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 80 || r.bottom > window.innerHeight - 40) el.scrollIntoView({
      block: 'center',
      behavior: 'auto'
    });
  }, [dayKey]);
  const top = (time: string) => (toMinutes(time) - start) * PX_PER_MINUTE;
  const hourCount = (end - start) / 60;
  const cols = `3rem repeat(${days.length}, minmax(0, 1fr))`;
  return <div className={`overflow-hidden rounded-lg border border-line bg-white ${drag.dragging ? 'cursor-grabbing select-none' : ''}`} data-id="element-209" data-mp="2kqjmjmgjdr4z">
      {showHeader && <div className="grid border-b border-line" style={{
      gridTemplateColumns: cols
    }} data-id="element-210" data-mp="2unoczyhf08rl">
          <span aria-hidden="true" data-id="element-211" data-mp="2pouyq3ub0dqd" />
          {days.map(d => {
        const day = salonDayFor(hours, site.salonExceptions, d);
        const open = Boolean(day);
        const count = shown.filter(a => a.date === d && isActive(a)).length;
        return <div key={d} className={`border-l border-line px-2 py-2.5 text-center ${open ? '' : 'bg-sand/60'}`} data-id="element-212" data-mp="2yngmqeijokyj">
                <span className="block text-[12px] capitalize text-muted" data-id="element-213" data-mp="38fclzswdah4c">{weekdayShort(fromIso(d))}</span>
                <span className={`mt-0.5 inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full px-1.5 font-display text-[16px] ${d === todayIso ? 'bg-ink text-shell' : 'text-ink'}`} data-id="element-214" data-mp="3meugmowcgolm">
                  {fromIso(d).getDate()}
                </span>
                {/* Wie er die dag werkt: initialen in haar kleur, de naam erbij voor wie het nog niet kent. */}
                {colored && day ? <span className="mt-0.5 flex items-center justify-center gap-1 text-[11px] text-ink" data-id="element-215" data-mp="38z3wy3xfeck">
                    <StaffDot staff={team} id={day.staffId} size="sm" data-id="element-216" data-mp="80h0l9yx2srq" />
                    <span className="truncate" data-id="element-217" data-mp="c1u8rqphf81t">{staffName(team, day.staffId)}</span>
                    {count > 0 && <span className="text-muted" data-id="element-218" data-mp="h0x2if00hruh">· {count}</span>}
                  </span> : <span className="block text-[11px] text-muted" data-id="element-219" data-mp="m09rp6ecvb08">{open ? count ? `${count} open` : '\u00a0' : 'Dicht'}</span>}
              </div>;
      })}
        </div>}

      <div className="grid" style={{
      gridTemplateColumns: cols
    }} data-id="element-220" data-mp="2nrwmzk5a5s2i">
        {/* Uren, met de huidige tijd als label op de nu-lijn */}
        <div className="relative" style={{
        height
      }} aria-hidden="true" data-id="element-221" data-mp="2sqzpawtvlk6s">
          {Array.from({
          length: hourCount
        }, (_, i) => {
          const at = start + i * 60;
          // Geen uurlabel onder het nu-label door.
          if (i === 0 || nowVisible && nowAt !== null && Math.abs(at - nowAt) < 12) return null;
          return <span key={i} className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-muted" style={{
            top: i * 60 * PX_PER_MINUTE
          }} data-id="element-222" data-mp="w46w91wox1yz">
                {toTime(at)}
              </span>;
        })}
          {nowVisible && nowAt !== null && <span className="absolute right-1 z-raised -translate-y-1/2 rounded-full bg-clay px-1.5 text-[10px] font-medium leading-4 tabular-nums text-shell" style={{
          top: (nowAt - start) * PX_PER_MINUTE
        }} data-id="element-223" data-mp="1oqhuw70qkst5">
              {toTime(nowAt)}
            </span>}
        </div>

        {days.map(d => {
        const w = salonDayFor(hours, site.salonExceptions, d);
        const dayItems = shown.filter(a => a.date === d);
        const lanes = layoutLanes(dayItems);
        const dayNow = d === todayIso && nowVisible ? nowAt : null;
        return <div key={d} ref={drag.registerColumn(d)} onClick={pick(d)} onMouseMove={track(d)} onMouseLeave={() => setHover(null)} className={`relative border-l border-line ${drag.dragging ? '' : 'cursor-copy'}`} style={{
          height
        }} aria-label={`Tijdlijn ${dayLong(d)}`} role="group" data-id="element-224" data-mp="28hdjx4e1qnh3">
              {/* Uur- en halfuurlijnen */}
              {Array.from({
            length: hourCount
          }, (_, i) => <span key={i} className="pointer-events-none absolute inset-x-0" style={{
            top: i * 60 * PX_PER_MINUTE
          }} data-id="element-225" data-mp="2cgctx3emxafe">
                  <span className="absolute inset-x-0 border-t border-line/70" data-id="element-226" data-mp="3574knlcxl6vc" />
                  <span className="absolute inset-x-0 border-t border-dashed border-line/40" style={{
              top: 30 * PX_PER_MINUTE
            }} data-id="element-227" data-mp="3551laapw21dy" />
                </span>)}
              {/* Buiten openingstijden: gedempt */}
              {!w ? <span className="pointer-events-none absolute inset-0 bg-sand/60" data-id="element-228" data-mp="tihogqq76f5g" /> : <>
                  <span className="pointer-events-none absolute inset-x-0 top-0 bg-sand/60" style={{
              height: Math.max(0, top(w.open))
            }} data-id="element-229" data-mp="187m5w5p64fci" />
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-sand/60" style={{
              top: Math.min(height, top(w.close))
            }} data-id="element-230" data-mp="fjhy9en1ds76" />
                </>}
              {/* Wat vandaag al voorbij is: lichtjes terug */}
              {dayNow !== null && <span className="pointer-events-none absolute inset-x-0 top-0 bg-shell/60" style={{
            height: (dayNow - start) * PX_PER_MINUTE
          }} data-id="element-231" data-mp="olr7m16kjfiz" />}

              {hover?.date === d && !drag.dragging && <span className="pointer-events-none absolute inset-x-1 flex items-center gap-1 rounded-md border border-dashed border-clay/50 bg-blush/40 px-2 text-[11px] leading-none tabular-nums text-ink" style={{
            top: (hover.minutes - start) * PX_PER_MINUTE,
            height: ADMIN_STEP_MINUTES * PX_PER_MINUTE
          }} aria-hidden="true" data-id="element-232" data-mp="1zxs3e01uo488">
                  <PlusIcon className="h-3 w-3" strokeWidth={2} data-id="element-233" data-mp="1prf7od0c0v3z" />
                  {toTime(hover.minutes)}
                </span>}

              {shownBlocks.filter(b => b.date === d).map(b => <button key={b.id} type="button" onClick={() => onSelectBlock(b)} className="absolute inset-x-1 flex items-start gap-1 overflow-hidden rounded-md border border-dashed border-line bg-sand px-2 py-1 text-left text-[12px] text-muted transition-colors duration-150 ease-soft hover:border-ink/30" style={{
            top: top(b.time),
            height: Math.max(18, b.durationMinutes * PX_PER_MINUTE - 2)
          }} data-id="element-234" data-mp="1avzk8qu9bxgk">
                    <LockIcon className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-235" data-mp="33sbnokg1w7m2" />
                    <span className="truncate" data-id="element-236" data-mp="38qmvkpmsc81n">{blockLabel(b.reason, b.label)}</span>
                  </button>)}

              {dayItems.map(a => {
            const lane = lanes.get(a.id) ?? {
              lane: 0,
              lanes: 1
            };
            const compact = a.durationMinutes < COMPACT_MINUTES || days.length > 1;
            const selected = a.id === selectedId;
            const lifted = ghost?.id === a.id;
            const draggable = Boolean(onMove) && drag.canDrag(a);
            const treatment = index.treatmentBySlug(a.treatmentSlug);
            return <button key={a.id} type="button" onClick={() => onSelect(a.id)} {...draggable ? drag.handlers(a) : {}} aria-pressed={selected} aria-label={`${a.time} tot ${endTime(a)}, ${a.customer.name}, ${treatment?.name ?? a.treatmentSlug}${colored ? `, bij ${staffName(team, a.staffId)}` : ''}, ${stateOf(a).label}`} className={`absolute overflow-hidden rounded-md border px-2 py-1 text-left transition-[box-shadow,opacity] duration-150 ease-soft hover:shadow-canvas ${blockCls(a, colored ? staffToneOf(team, a.staffId) : undefined)} ${selected ? 'z-raised ring-2 ring-ink ring-offset-1' : ''} ${draggable ? 'select-none [-webkit-touch-callout:none]' : ''} ${draggable && !drag.dragging ? 'cursor-grab' : ''} ${lifted ? 'opacity-40' : ''}`} style={{
              top: top(a.time) + 1,
              height: itemHeight(a.durationMinutes),
              left: `calc(${lane.lane / lane.lanes * 100}% + 4px)`,
              width: `calc(${100 / lane.lanes}% - 8px)`
            }} data-id="element-237" data-mp="2yutecsy4fqdj">
                    <span className="flex items-center gap-1 text-[11px] tabular-nums text-muted" data-id="element-238" data-mp="3fegw7xbwjngr">
                      {colored && <StaffDot staff={team} id={a.staffId} size="sm" data-id="element-239" data-mp="3k5gvvaceojmy" />}
                      {a.time}
                      {!compact && `–${endTime(a)}`}
                      {a.kind === 'video' && <VideoIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-240" data-mp="3bcn2k904jjq3" />}
                      {a.customer.firstVisit && isActive(a) && <span className="rounded-full bg-clay px-1.5 text-[10px] leading-4 text-shell" data-id="element-241" data-mp="2xv8eus9eh62">Nieuw</span>}
                    </span>
                    <span className={`block truncate text-[13px] leading-tight ${a.outcome === 'no-show' ? 'line-through' : ''}`} data-id="element-242" data-mp="6wazpavdibpa">{a.customer.name}</span>
                    {!compact && <span className="block truncate text-[12px] text-muted" data-id="element-243" data-mp="3u1tlnx4tydcf">{treatment?.name ?? a.treatmentSlug}</span>}
                  </button>;
          })}

              {/* De opgetilde afspraak: waar hij landt, vastgeklikt op het kwartier. */}
              {ghost && ghostAppt && ghost.date === d && <div className={`pointer-events-none absolute inset-x-1 z-raised scale-[1.02] overflow-hidden rounded-md border-2 px-2 py-1 shadow-canvas transition-[top] duration-100 ease-soft motion-reduce:transition-none ${ghost.conflicts.length || ghost.outside ? 'border-clay bg-white' : 'border-ink/70 bg-blush'}`} style={{
            top: top(ghost.time) + 1,
            height: itemHeight(ghostAppt.durationMinutes)
          }} aria-hidden="true" data-id="element-244" data-mp="2xzpj5sj1heod">
                  <span className="block text-[11px] font-medium tabular-nums text-ink" data-id="element-245" data-mp="37v9as2qklr7b">
                    {ghost.time}–{endTime({
                time: ghost.time,
                durationMinutes: ghostAppt.durationMinutes
              })}
                  </span>
                  <span className="block truncate text-[13px] leading-tight text-ink" data-id="element-246" data-mp="1whn9jj7frz5f">{firstName(ghostAppt.customer.name)}</span>
                  {ghost.conflicts.length > 0 && <span className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-ink" data-id="element-247" data-mp="1he40c2nca09h">
                      <AlertTriangleIcon className="h-3 w-3 shrink-0" strokeWidth={1.75} data-id="element-248" data-mp="1mbvu727jnwgb" />
                      Botst
                    </span>}
                </div>}

              {dayNow !== null && <span ref={nowRef} className="pointer-events-none absolute inset-x-0 z-raised flex items-center" style={{
            top: (dayNow - start) * PX_PER_MINUTE
          }} aria-hidden="true" data-id="element-249" data-mp="1w3t6yde7awh5">
                  <span className="-ml-1 h-2 w-2 rounded-full bg-clay" data-id="element-250" data-mp="251vc954ywba8" />
                  <span className="h-px flex-1 bg-clay" data-id="element-251" data-mp="2a1zx17cwrs69" />
                </span>}
            </div>;
      })}
      </div>
      <p className="sr-only" data-id="element-252" data-mp="2f0kwv5o909u6">
        Klik op een lege plek om op die tijd een afspraak te maken, of gebruik de knop Nieuwe afspraak. Verzetten kan met slepen, of via het paneel van de afspraak.
      </p>
    </div>;
}